import { addDays, differenceInCalendarDays, format, parseISO } from 'date-fns';

import type { ChartData, ChartSeries, DashboardOptions } from '@/entities/dashboard';

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATE_FORMAT = 'yyyy-MM-dd';
/** How legacy RITA printed a date on its charts (`Constants.DATE_FORMAT_SHORT`, English month names). */
const LEGACY_DATE_FORMAT = 'dd/MMM/yy';
/** A week-grouped chart carries the first day of each week, so its step is 7. */
const WEEK_STEP_DAYS = 7;
/**
 * Beyond roughly three years of daily categories the axis is a wall you can only
 * read by zooming, and inventing the empty days in between costs render time
 * without buying legibility. Past this the sparse axis stays.
 */
const MAX_FILLED_CATEGORIES = 1000;

/**
 * Checks whether every category is an ISO date (YYYY-MM-DD), i.e. the chart is
 * grouped by date/week rather than by a categorical dimension.
 */
export const isDateCategories = (categories: string[]): boolean =>
  categories.length > 0 && categories.every((category) => ISO_DATE_PATTERN.test(category));

/** An ISO date category as legacy RITA labelled it: `05/Oct/26`. */
export const formatDateCategory = (category: string): string => format(parseISO(category), LEGACY_DATE_FORMAT);

/**
 * A week category as legacy RITA named it: from the day it starts on to the same
 * weekday a week later, `28/Sep/26 - 05/Oct/26`.
 */
export const formatWeekCategory = (category: string): string =>
  `${formatDateCategory(category)} - ${format(addDays(parseISO(category), WEEK_STEP_DAYS), LEGACY_DATE_FORMAT)}`;

/**
 * Sorts date-grouped chart data chronologically (oldest first). The API returns
 * date categories newest-first; series data points are aligned with categories
 * by index, so both are reordered together. Categorical data is returned as-is.
 */
export const sortChartDataByDate = (data: ChartData): ChartData => {
  if (!isDateCategories(data.categories)) {
    return data;
  }

  const order = data.categories
    .map((category, index) => ({ category, index }))
    .sort((a, b) => a.category.localeCompare(b.category));

  return {
    ...data,
    categories: order.map((entry) => entry.category),
    series: data.series.map((series) => ({
      ...series,
      data: order.map((entry) => series.data[entry.index])
    }))
  };
};

/**
 * Puts back the dates the API never sent. Categories are built server-side from
 * the rows the query returned, so a day nothing moved on is missing from the axis
 * altogether instead of standing there at zero (353356).
 *
 * How far the axis may be stretched depends on how the range was picked. A custom
 * range reaches the API as `fromDate`/`toDate` and is parsed there verbatim, so
 * both ends are known exactly and the axis is filled out to them. A preset is
 * resolved by server-side rules of its own — `LW` starts six days back, not the
 * picker's seven, and `FE` has no lower bound at all — so there only the gaps
 * between the days that did arrive are filled, and the ends stay where the data
 * ends. Anything else would print a zero for a day the query never covered.
 */
export const fillDateCategories = (
  data: ChartData,
  { group, period, fromDate, toDate }: Pick<DashboardOptions, 'group' | 'period' | 'fromDate' | 'toDate'>
): ChartData => {
  if (!isDateCategories(data.categories)) {
    return data;
  }

  const dated = group === 'W' ? { ...data, weekly: true } : data;
  const known = [...data.categories].sort();
  const customRange = period === 'CR' && Boolean(fromDate) && Boolean(toDate);
  const from = customRange && fromDate! < known[0] ? fromDate! : known[0];
  const to = customRange && toDate! > known[known.length - 1] ? toDate! : known[known.length - 1];

  // Stepping out from a day the API did send keeps the phase of a week-grouped
  // axis, whose weeks the backend counts from today rather than from the range.
  const anchor = parseISO(known[0]);
  const step = group === 'W' ? WEEK_STEP_DAYS : 1;
  const before = Math.max(0, Math.floor(differenceInCalendarDays(anchor, parseISO(from)) / step));
  const after = Math.max(0, Math.floor(differenceInCalendarDays(parseISO(to), anchor) / step));

  if (before + after + 1 > MAX_FILLED_CATEGORIES || before + after + 1 === data.categories.length) {
    return dated;
  }

  const categories = Array.from({ length: before + after + 1 }, (_, index) =>
    format(addDays(anchor, (index - before) * step), ISO_DATE_FORMAT)
  );

  return {
    ...dated,
    categories,
    series: data.series.map((series) => {
      // Points are aligned with categories by index, the same contract
      // `sortChartDataByDate` reorders them under.
      const valueByCategory = new Map(data.categories.map((category, index) => [category, series.data[index]?.value]));

      return {
        ...series,
        data: categories.map((category) => ({ category, value: valueByCategory.get(category) ?? 0 }))
      };
    })
  };
};

/**
 * Returns per-series colors only when every series has a distinct color;
 * otherwise returns undefined for all so the ECharts palette assigns
 * distinguishable colors. Colors come from the frontend vehicle-category
 * palette (transport) or the API (storage charts); either source can leave
 * gaps or duplicates — e.g. unmapped categories, or legacy ids 1/4/7
 * sharing one color.
 */
export const resolveSeriesColors = (series: ChartSeries[]): (string | undefined)[] => {
  const colors = series.map((item) => item.color);
  const defined = colors.filter((color): color is string => Boolean(color));
  const allDistinct = defined.length === series.length && new Set(defined).size === defined.length;

  return allDistinct ? colors : series.map(() => undefined);
};
